import { useState } from "react";
import { motion } from "framer-motion";
import { ShieldCheck, ShieldAlert, Info, CheckCircle2, AlertTriangle } from "lucide-react";
import { RoomShell } from "@/components/RoomShell";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

const MEDIA_TYPES = ["Movie", "TV Show", "Song", "Video Game", "Book", "Other"];

const LEVEL_STYLE = {
  none: { bg: "rgba(74,222,128,0.08)", border: "rgba(74,222,128,0.35)", text: "#4ade80", label: "None" },
  mild: { bg: "rgba(245,158,11,0.08)", border: "rgba(245,158,11,0.35)", text: "#F59E0B", label: "Mild" },
  moderate: { bg: "rgba(234,88,12,0.10)", border: "rgba(234,88,12,0.4)", text: "#fb923c", label: "Moderate" },
  strong: { bg: "rgba(153,27,27,0.15)", border: "rgba(248,113,113,0.5)", text: "#f87171", label: "Strong" },
};

export default function ParentCenter() {
  const [form, setForm] = useState({ title: "", media_type: "Movie", details: "" });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const run = async (e) => {
    e.preventDefault();
    setLoading(true);
    setResult(null);
    try {
      const { data } = await api.post("/content-scan", form);
      setResult(data.result);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const field = "w-full rounded-md px-4 py-3 text-[#FDF6E3] outline-none";
  const fieldStyle = { background: "var(--wood-dark)", border: "1px solid var(--wood-medium)" };
  const safe = result?.child_appropriate;

  return (
    <RoomShell testid="parent-center" kicker="Parent Intelligence Center" title="Content Advisory">
      <p className="text-[#A89A80] max-w-2xl -mt-6 mb-10 leading-relaxed">
        Enter any song, movie, game, show, or book. The advisory reports which mature themes appear and how strongly —
        so you decide what's right for your family. Neutral and factual; you make the final call.
      </p>

      <div className="grid lg:grid-cols-2 gap-8">
        <form onSubmit={run} className="space-y-4">
          <input data-testid="content-title-input" required placeholder="Title (e.g. 'Stranger Things')"
            className={field} style={fieldStyle} value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })} />

          <div className="flex flex-wrap gap-2">
            {MEDIA_TYPES.map((t) => (
              <button key={t} type="button" data-testid={`content-type-${t}`}
                onClick={() => setForm({ ...form, media_type: t })}
                className="rounded-full px-3 py-1.5 font-typewriter text-[10px] uppercase tracking-widest transition-colors"
                style={{
                  background: form.media_type === t ? "#991B1B" : "var(--wood-dark)",
                  color: form.media_type === t ? "#FDF6E3" : "#A89A80",
                  border: "1px solid var(--wood-medium)",
                }}>
                {t}
              </button>
            ))}
          </div>

          <textarea data-testid="content-details-input" rows={5} maxLength={2000}
            placeholder="Optional: paste a description, lyrics, or notes to improve accuracy…"
            className={field} style={fieldStyle} value={form.details}
            onChange={(e) => setForm({ ...form, details: e.target.value })} />

          <button data-testid="content-submit-btn" type="submit" disabled={loading}
            className="w-full rounded-md py-3 font-typewriter text-sm uppercase tracking-[0.25em] transition-colors disabled:opacity-60"
            style={{ background: "#991B1B", color: "#FDF6E3" }}>
            {loading ? "Reviewing the file…" : "Run Content Advisory"}
          </button>
          <p className="text-xs text-[#6b5f4d] text-center">Free to use — no account needed.</p>
        </form>

        <div>
          {!result && !loading && (
            <div className="rounded-md p-8 h-full flex flex-col items-center justify-center text-center"
              style={{ background: "var(--wood-dark)", border: "1px dashed var(--wood-medium)" }}>
              <ShieldCheck size={40} className="text-[#2C1A0F] mb-4" />
              <p className="font-serif-hq text-xl text-[#A89A80]">Awaiting a title to review.</p>
            </div>
          )}

          {loading && (
            <div className="rounded-md p-8 h-full flex items-center justify-center" style={{ background: "var(--wood-dark)", border: "1px solid var(--wood-medium)" }}>
              <p className="font-typewriter text-sm uppercase tracking-widest text-[#F59E0B] animate-pulse">Reviewing…</p>
            </div>
          )}

          {result && (
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} data-testid="content-result" className="space-y-4">
              <div className="rounded-md p-6" style={{ background: "var(--wood-dark)", border: `1px solid ${safe ? "rgba(74,222,128,0.4)" : "rgba(248,113,113,0.4)"}` }}>
                <div className="flex items-center gap-3 mb-3">
                  {safe ? <ShieldCheck size={26} className="text-[#4ade80]" /> : <ShieldAlert size={26} className="text-[#f87171]" />}
                  <div>
                    <p className="font-serif-hq text-2xl text-[#FDF6E3] leading-none">
                      {safe ? "Generally family-friendly" : "Parental discretion advised"}
                    </p>
                    <p className="font-typewriter text-[10px] uppercase tracking-widest text-[#A89A80] mt-1.5">
                      Suggested age {result.recommended_min_age ?? "—"}+ · Confidence: {result.confidence || "—"}
                    </p>
                  </div>
                </div>
                <p className="text-sm text-[#E6D5B8] leading-relaxed">{result.summary}</p>
              </div>

              <div className="grid gap-2.5" data-testid="content-flags">
                {(result.flags || []).map((f, i) => {
                  const s = LEVEL_STYLE[f.level] || LEVEL_STYLE.none;
                  return (
                    <div key={i} data-testid={`flag-${i}`} className="rounded-md p-4 flex items-start gap-3"
                      style={{ background: s.bg, border: `1px solid ${s.border}` }}>
                      {f.present ? <AlertTriangle size={16} style={{ color: s.text }} className="shrink-0 mt-0.5" />
                        : <CheckCircle2 size={16} className="text-[#4ade80] shrink-0 mt-0.5" />}
                      <div className="flex-1">
                        <div className="flex items-center justify-between gap-2">
                          <p className="text-sm font-medium text-[#FDF6E3]">{f.category}</p>
                          <span className="font-typewriter text-[9px] uppercase tracking-widest px-2 py-0.5 rounded-full shrink-0"
                            style={{ color: s.text, border: `1px solid ${s.border}` }}>{s.label}</span>
                        </div>
                        {f.note && <p className="text-xs text-[#A89A80] mt-1 leading-relaxed">{f.note}</p>}
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="rounded p-4 flex gap-3" style={{ background: "var(--deep-shadow)" }}>
                <Info size={16} className="text-[#A89A80] shrink-0 mt-0.5" />
                <p className="text-xs text-[#6b5f4d] leading-relaxed">
                  This is AI-assisted guidance based on the title and any details you provided. Always use your own judgment as the parent.
                </p>
              </div>
            </motion.div>
          )}
        </div>
      </div>
    </RoomShell>
  );
}
